import { Button } from '@/components/ui/button';
import { ArrowRight, Clock, Eye, MapPin, Shield, TrendingUp } from 'lucide-react';

import { Link, useLocation } from 'wouter';
import SplashLayout from '@/components/splash-screen/SplashLayout';
import { TestimonialsSlider } from '@/components/splash-screen/TestimonialsSlider';
import TrustedPartnersCarousel from '@/components/splash-screen/TrustedPartnersCarousel';
import FAQSection from '@/components/splash-screen/FAQSection';
import { useAuth } from '@/hooks/use-auth';
import { useEffect, useState, useRef } from 'react';

import {
  dashboard2,
  tickicon,
  transcript_page,
  lobby,
  ai_report_page,
  tags,
} from '@/assets/images';
import GetInTouch from '@/components/splash-screen/GetInTouch';
import VideoShowcase from '@/components/landing/video-showcase';
import _ from 'lodash';

const industryFirstFeatures = [
  {
    number: '1',
    title: 'Real-Time Alerts & Transcripts',
    subtitle: 'First & Only 50-State Provider',
    description:
      'Get instant alerts on chamber and floor hearings across all 50 states, complete with real-time transcripts and video clips of legislative events as they happen.',
    features: [
      'Live chamber alerts across all 50 states',
      'Real-time transcript generation',
      'Instant video clips of key moments',
      'Comprehensive hearing coverage',
    ],
    image: transcript_page,
  },
  {
    number: '2',
    title: 'Advocate Management Portal',
    subtitle: 'First & Only 50-State Provider',
    description:
      'Leverage your lobbyist stable to its fullest extent with our comprehensive management portal that tracks relationships, testimony, and strategic coverage.',
    features: [
      'Complete lobbyist relationship mapping',
      'Testimony monitoring and analysis',
      'Strategic chamber coverage optimization',
      'Contribution tracking and analysis',
    ],
    image: lobby,
  },
  {
    number: '3',
    title: 'Connected Intelligence Platform',
    subtitle: 'First & Only 50-State Provider',
    description:
      'Access the only platform that connects bills, transcripts, testimony, lobbying, and campaign finance into one searchable intelligence layer. Generate AI reports and uncover insights impossible to find elsewhere.',
    features: [
      '30+ AI-powered report templates',
      'Connected datasets no one else has',
      'Stakeholder mapping and risk assessment',
      'Hidden relationships and patterns',
    ],
    image: ai_report_page,
  },
  {
    number: '4',
    title: 'Smart Bill Tracking',
    subtitle: 'First Agentic Tracking Service',
    description:
      'Tag, categorize, and track bills with our intelligent system. Agentic Track ensures you never miss relevant legislation while custom tags keep everything organized.',
    features: [
      'Automatic bill identification',
      'Custom tagging system',
      'Team collaboration tools',
      'Zero manual setup required',
    ],
    image: tags,
  },
];

const stats = [
  { number: '50', label: 'States Covered', subtext: 'Zero blind spots.', suffix: '', icon: MapPin },
  {
    number: '24/7',
    label: 'Real-Time Alerts',
    subtext: 'As events happen.',
    suffix: '',
    icon: Eye,
  },
  {
    number: '200',
    label: 'Events Daily',
    subtext: 'Captured nationwide.',
    suffix: '+',
    icon: Clock,
  },
  {
    number: '1000',
    label: 'Bills Tracked',
    subtext: 'Tailored to your issues.',
    suffix: '+',
    icon: TrendingUp,
  },
  {
    number: '10',
    label: 'Datasets Unified',
    subtext: 'Unified intelligence.',
    suffix: '+',
    icon: Shield,
  },
];

export default function LegisIQLanding() {
  const { isAuthenticated } = useAuth();
  const [, navigate] = useLocation();

  // Animation states
  const [typedText, setTypedText] = useState('');
  const [showUnderline, setShowUnderline] = useState(false);
  const [visibleWords, setVisibleWords] = useState(0);

  // UnifiedPlatform scroll animation states
  const [scrollProgress, setScrollProgress] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string | null>('Bill Agent');
  const sectionRef = useRef<HTMLDivElement>(null);

  // ProblemSolution scroll animation states
  const [problemHeaderVisible, setProblemHeaderVisible] = useState(false);
  const [visibleProblems, setVisibleProblems] = useState<number[]>([]);
  const problemHeaderRef = useRef<HTMLDivElement>(null);
  const problemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Determine which layers should be visible based on scroll progress
  const visibleLayers = {
    1: scrollProgress > 0.15, // LegisIQ appears early
    2: scrollProgress > 0.3, // Agents
    3: scrollProgress > 0.4, // Tools
    4: scrollProgress > 0.45, // Data Sources
  };

  // Mapping of agents to their related tools and data sources
  const agentMapping: Record<string, { tools: string[]; data: string[] }> = {
    'Bill Agent': {
      tools: [
        'Agentic Track',
        'AI Bill Brief',
        'Bill Comparison',
        'Reports',
        'Company Impact Analysis',
      ],
      data: ['Bills', 'Statutes & Regulations'],
    },
    'Committee Agent': {
      tools: ['Live Transcripts & Alerts', 'Live Alerts', 'Deep Relationship Mapping', 'Reports'],
      data: ['Committee & Chamber Hearings', 'Committees'],
    },
  };

  const isHighlighted = (itemName: string, type: 'tool' | 'data') => {
    if (!selectedAgent) return false;
    const mapping = agentMapping[selectedAgent];
    if (!mapping) return false;
    return type === 'tool' ? mapping.tools.includes(itemName) : mapping.data.includes(itemName);
  };

  // If user is already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Typewriter effect for tagline
  useEffect(() => {
    const taglineText = 'One Mission - Simplify Government Affairs.';
    let currentIndex = 0;

    const typeInterval = setInterval(() => {
      if (currentIndex <= taglineText.length) {
        setTypedText(taglineText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typeInterval);
        // Show underline after typing completes
        setTimeout(() => setShowUnderline(true), 200);
      }
    }, 50); // 50ms per character

    return () => clearInterval(typeInterval);
  }, []);

  // Stagger word animations on mount (starts after typewriter)
  useEffect(() => {
    const headlineWords = 'Stay Ahead with Unified Legislative Intelligence'.split(' ');
    headlineWords.forEach((_, index) => {
      setTimeout(
        () => {
          setVisibleWords(index + 1);
        },
        2300 + index * 80,
      ); // Start after 2.3s typewriter, then 80ms delay between words
    });
  }, []);

  // UnifiedPlatform scroll animation
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const sectionHeight = rect.height;

      // Calculate scroll progress
      const scrollStart = windowHeight - rect.top;
      const scrollRange = windowHeight + sectionHeight;
      const progress = Math.max(0, Math.min(1, scrollStart / scrollRange));

      setScrollProgress(progress);

      // Trigger header animation when section enters viewport
      if (rect.top < windowHeight * 0.7 && !headerVisible) {
        setHeaderVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, [headerVisible]);

  // ProblemSolution scroll animation with IntersectionObserver
  useEffect(() => {
    const observers = problemRefs.current.map((ref, index) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleProblems((prev) => _.uniq([...prev, index]));
          }
        },
        {
          threshold: 0.2,
          rootMargin: '0px 0px -100px 0px',
        },
      );

      if (ref) observer.observe(ref);
      return observer;
    });

    // Header observer
    const headerObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setProblemHeaderVisible(true);
        }
      },
      {
        threshold: 0.3,
      },
    );

    if (problemHeaderRef.current) headerObserver.observe(problemHeaderRef.current);

    return () => {
      observers.forEach((observer) => observer.disconnect());
      headerObserver.disconnect();
    };
  }, []);

  // Load Vimeo player script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector(
        'script[src="https://player.vimeo.com/api/player.js"]',
      );
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <SplashLayout>
      <div className="bg-white">
        {/* Hero Section */}
        <section className="relative bg-white overflow-hidden pt-32 pb-20">
          {/* Subtle background decoration */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-50/50 to-white pointer-events-none"></div>

          <div className="relative container mx-auto px-4 py-12 lg:py-16">
            <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
              {/* Left side - Text content */}
              <div className="text-center lg:text-left">
                <div className="mb-6">
                  <p className="font-serif text-xl text-gray-700 leading-relaxed">
                    <span className="relative inline-block whitespace-nowrap">
                      <span className="relative z-10">
                        {typedText}
                        <span
                          className="inline-block w-0.5 h-6 bg-[#102A43] ml-1 align-text-bottom"
                          style={{
                            opacity: typedText.length >= 44 ? 0 : 1,
                            animation:
                              typedText.length >= 44 ? 'none' : 'blink 0.8s ease-in-out infinite',
                          }}
                        ></span>
                      </span>
                      <style>{`
                        @keyframes blink {
                          0%, 49% { opacity: 1; }
                          50%, 100% { opacity: 0; }
                        }
                      `}</style>
                      <span
                        className="absolute -bottom-1 left-0 h-2 bg-[#102A43]/30 transition-all duration-500"
                        style={{
                          width: showUnderline ? '100%' : '0%',
                        }}
                      ></span>
                    </span>
                  </p>
                </div>

                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-8 text-black">
                  {'Stay Ahead with Unified Legislative Intelligence'
                    .split(' ')
                    .map((word, index) => (
                      <span
                        key={index}
                        className={`inline-block transition-all duration-500 ${
                          index < visibleWords
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-0 translate-y-2'
                        }`}
                        style={{ marginRight: '0.25em' }}
                      >
                        {word}
                      </span>
                    ))}
                </h1>

                <p
                  className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-12 transition-all duration-700"
                  style={{
                    opacity: visibleWords >= 1 ? 1 : 0,
                    transform: visibleWords >= 1 ? 'translateY(0)' : 'translateY(10px)',
                    transitionDelay: '100ms',
                  }}
                >
                  Legacy bill tracking is fragmented. LegisIQ's AI-powered workspace empowers policy
                  professionals to move faster and smarter across any state by combining
                  traditionally siloed technologies and expanding on them to create one powerful
                  solution.
                </p>

                <div
                  className="transition-all duration-700"
                  style={{
                    opacity: visibleWords >= 1 ? 1 : 0,
                    transform: visibleWords >= 1 ? 'translateY(0)' : 'translateY(10px)',
                    transitionDelay: '200ms',
                  }}
                >
                  <Button
                    size="lg"
                    onClick={() => {
                      window.open('https://calendly.com/william-legisiq/30min', '_blank');
                    }}
                    className="bg-[#102A43] hover:bg-[#0d1f32] text-white px-10 py-6 text-xl font-bold rounded-xl shadow-lg transition-all duration-300 hover:scale-105"
                  >
                    Book a Demo
                  </Button>
                </div>
              </div>

              {/* Right side - 3 Agents Side by Side */}
              <div className="relative flex flex-col items-center gap-8 hidden lg:flex">
                {/* Radial gradient background */}
                <div
                  className="absolute inset-0 -inset-x-16 -inset-y-8 pointer-events-none"
                  style={{
                    background:
                      'radial-gradient(circle at 50% 40%, rgba(16, 42, 67, 0.08) 0%, rgba(255, 255, 255, 0) 70%)',
                  }}
                ></div>

                {/* Agent intro text */}
                <div
                  className="relative z-10 text-center transition-all duration-700"
                  style={{
                    opacity: visibleWords >= 6 ? 1 : 0,
                    transform: visibleWords >= 6 ? 'translateY(0)' : 'translateY(20px)',
                    transitionDelay: '0ms',
                  }}
                >
                  <p className="font-serif text-gray-700 text-xl leading-relaxed mb-2">
                    The deepest toolset. The richest dataset.
                  </p>
                  <p className="text-sm text-gray-600">
                    Featuring legislative intelligence agents that work for you 24/7
                  </p>
                </div>

                <div className="relative z-10 grid grid-cols-2 gap-8 lg:gap-12">
                  {/* Bill Agent */}
                  <div
                    className="flex flex-col items-center transition-all duration-700"
                    style={{
                      opacity: visibleWords >= 6 ? 1 : 0,
                      transform: visibleWords >= 6 ? 'translateY(0)' : 'translateY(20px)',
                      transitionDelay: '0ms',
                    }}
                  >
                    <div className="relative">
                      <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full flex items-center justify-center shadow-xl border-4 border-white mb-4 overflow-hidden bg-white transition-all duration-300 hover:scale-110 hover:shadow-2xl">
                        <img
                          src="/bill_agent.png"
                          alt="Bill Agent"
                          className="w-full h-full object-contain scale-110"
                        />
                      </div>
                    </div>
                    <div className="mb-3 h-[60px] flex flex-col justify-center">
                      <p className="text-sm lg:text-base font-semibold text-center mb-1 text-gray-600">
                        Bill Agent
                      </p>
                      <p className="text-s text-center font-serif text-gray-500">
                        Identifying and analyzing legislation
                      </p>
                    </div>
                    <div className="space-y-1.5 w-full max-w-[200px] mt-4">
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 text-center mb-2 font-semibold">
                        Powered By
                      </p>
                      <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1 text-center">
                        <span className="text-[11px] text-gray-600">Agentic Track</span>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1 text-center">
                        <span className="text-[11px] text-gray-600">Bill Comparison</span>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1 text-center">
                        <span className="text-[11px] text-gray-600">AI Bill Brief</span>
                      </div>
                    </div>
                  </div>

                  {/* Committee Agent */}
                  <div
                    className="flex flex-col items-center transition-all duration-700"
                    style={{
                      opacity: visibleWords >= 6 ? 1 : 0,
                      transform: visibleWords >= 6 ? 'translateY(0)' : 'translateY(20px)',
                      transitionDelay: '150ms',
                    }}
                  >
                    <div className="relative">
                      <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full flex items-center justify-center shadow-xl border-4 border-white mb-4 overflow-hidden bg-white transition-all duration-300 hover:scale-110 hover:shadow-2xl">
                        <img
                          src="/transcript_agent.png"
                          alt="Committee Agent"
                          className="w-full h-full object-contain scale-110"
                        />
                      </div>
                    </div>
                    <div className="mb-3 h-[60px] flex flex-col justify-center">
                      <p className="text-sm lg:text-base font-semibold text-center mb-1 text-gray-600">
                        Committee Agent
                      </p>
                      <p className="text-s text-center font-serif text-gray-500">
                        Watching committees so you don't have to
                      </p>
                    </div>
                    <div className="space-y-1.5 w-full max-w-[200px] mt-4">
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 text-center mb-2 font-semibold">
                        Powered By
                      </p>
                      <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1 text-center">
                        <span className="text-[11px] text-gray-600">Transcript Hub</span>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1 text-center">
                        <span className="text-[11px] text-gray-600">State Agendas</span>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1 text-center">
                        <span className="text-[11px] text-gray-600">Bill Tracking</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Video Showcase Section */}
        <VideoShowcase />

        {/* UnifiedPlatform Section */}
        <section ref={sectionRef} className="py-24 bg-gradient-to-b from-white to-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              {/* Header */}
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold text-[#102A43] mb-6">
                  {[
                    'Power your legislative efforts with one platform.',
                    'Working for you at all times.',
                    'Across all facets of the political sphere.',
                  ].map((part, index) => (
                    <span
                      key={index}
                      className="block"
                      style={{
                        opacity: headerVisible ? 1 : 0,
                        transform: headerVisible ? 'translateY(0)' : 'translateY(20px)',
                        transition: `all 0.8s ease-out ${index * 0.3}s`,
                      }}
                    >
                      {part}
                    </span>
                  ))}
                </h2>
                <p
                  className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
                  style={{
                    opacity: headerVisible ? 1 : 0,
                    transform: headerVisible ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.8s ease-out 0.9s',
                  }}
                >
                  Our agents and tools exist as part of a complete platform — a single, cohesive
                  intelligence layer for every policy professional.
                </p>
              </div>

              {/* Layered Stack Architecture */}
              <div className="border border-gray-200 rounded-xl bg-white shadow-lg overflow-hidden mb-16">
                {/* Layer 1: LegisIQ Platform */}
                <div
                  className={`border-b border-gray-300 px-6 py-2.5 bg-gradient-to-b from-slate-50 to-white shadow-sm transition-all duration-1000 ${
                    visibleLayers[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                >
                  <div className="text-xs font-bold text-gray-900 mb-1.5">One Unified Platform</div>
                  <div className="flex items-center justify-center py-1.5">
                    <img
                      src="/legisiq_blue_transparent.png"
                      alt="LegisIQ"
                      className="h-12 w-auto object-contain"
                    />
                  </div>
                </div>

                {/* Layer 2: AI Agents */}
                <div
                  className={`border-b border-gray-200 px-6 py-5 bg-gray-50 transition-all duration-1000 ${
                    visibleLayers[2] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                >
                  <div className="text-xs font-bold text-gray-900 mb-4">Agents</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div
                      onMouseEnter={() => setSelectedAgent('Bill Agent')}
                      onMouseLeave={() => setSelectedAgent(null)}
                      className={`border rounded-md bg-gradient-to-b from-blue-50/40 to-white px-3 py-2.5 transition-all cursor-pointer ${
                        selectedAgent === 'Bill Agent'
                          ? 'border-blue-600 shadow-lg ring-2 ring-blue-400'
                          : 'border-blue-200 hover:border-blue-400'
                      }`}
                    >
                      <div
                        className={`font-medium text-xs text-center ${selectedAgent === 'Bill Agent' ? 'text-blue-700 font-bold' : 'text-gray-900'}`}
                      >
                        Bill Agent
                      </div>
                    </div>
                    <div
                      onMouseEnter={() => setSelectedAgent('Committee Agent')}
                      onMouseLeave={() => setSelectedAgent(null)}
                      className={`border rounded-md bg-gradient-to-b from-blue-50/40 to-white px-3 py-2.5 transition-all cursor-pointer ${
                        selectedAgent === 'Committee Agent'
                          ? 'border-blue-600 shadow-lg ring-2 ring-blue-400'
                          : 'border-blue-200 hover:border-blue-400'
                      }`}
                    >
                      <div
                        className={`font-medium text-xs text-center ${selectedAgent === 'Committee Agent' ? 'text-blue-700 font-bold' : 'text-gray-900'}`}
                      >
                        Committee Agent
                      </div>
                    </div>
                  </div>
                </div>

                {/* Layer 3: Tools */}
                <div
                  className={`border-b border-gray-200 px-6 py-5 bg-gray-100 transition-all duration-1000 ${
                    visibleLayers[3] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                >
                  <div className="text-xs font-bold text-gray-900 mb-4">Tools</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <div
                      className={`border rounded bg-gradient-to-b from-purple-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('Agentic Track', 'tool')
                          ? 'border-purple-600 shadow-md'
                          : 'border-purple-200 hover:border-blue-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('Agentic Track', 'tool') ? 'text-purple-900 font-bold' : 'text-gray-700'}`}
                      >
                        Agentic Track
                      </div>
                    </div>
                    <div
                      className={`border rounded bg-gradient-to-b from-purple-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('Advocacy Management', 'tool')
                          ? 'border-purple-600 shadow-md'
                          : 'border-purple-200 hover:border-blue-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('Advocacy Management', 'tool') ? 'text-purple-900 font-bold' : 'text-gray-700'}`}
                      >
                        Advocacy Management
                      </div>
                    </div>
                    <div
                      className={`border rounded bg-gradient-to-b from-purple-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('AI Bill Brief', 'tool')
                          ? 'border-purple-600 shadow-md'
                          : 'border-purple-200 hover:border-blue-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('AI Bill Brief', 'tool') ? 'text-purple-900 font-bold' : 'text-gray-700'}`}
                      >
                        AI Bill Brief
                      </div>
                    </div>
                    <div
                      className={`border rounded bg-gradient-to-b from-purple-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('Bill Comparison', 'tool')
                          ? 'border-purple-600 shadow-md'
                          : 'border-purple-200 hover:border-blue-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('Bill Comparison', 'tool') ? 'text-purple-900 font-bold' : 'text-gray-700'}`}
                      >
                        Bill Comparison
                      </div>
                    </div>
                    <div
                      className={`border rounded bg-gradient-to-b from-purple-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('Reports', 'tool')
                          ? 'border-purple-600 shadow-md'
                          : 'border-purple-200 hover:border-blue-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('Reports', 'tool') ? 'text-purple-900 font-bold' : 'text-gray-700'}`}
                      >
                        Reports
                      </div>
                    </div>
                    <div
                      className={`border rounded bg-gradient-to-b from-purple-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('Company Impact Analysis', 'tool')
                          ? 'border-purple-600 shadow-md'
                          : 'border-purple-200 hover:border-blue-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('Company Impact Analysis', 'tool') ? 'text-purple-900 font-bold' : 'text-gray-700'}`}
                      >
                        Company Impact Analysis
                      </div>
                    </div>
                    <div
                      className={`border rounded bg-gradient-to-b from-purple-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('Deep Relationship Mapping', 'tool')
                          ? 'border-purple-600 shadow-md'
                          : 'border-purple-200 hover:border-blue-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('Deep Relationship Mapping', 'tool') ? 'text-purple-900 font-bold' : 'text-gray-700'}`}
                      >
                        Deep Relationship Mapping
                      </div>
                    </div>
                    <div
                      className={`border rounded bg-gradient-to-b from-purple-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('Live Alerts', 'tool')
                          ? 'border-purple-600 shadow-md'
                          : 'border-purple-200 hover:border-blue-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('Live Alerts', 'tool') ? 'text-purple-900 font-bold' : 'text-gray-700'}`}
                      >
                        Live Alerts
                      </div>
                    </div>
                    <div
                      className={`border rounded bg-gradient-to-b from-purple-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('Live Transcripts & Alerts', 'tool')
                          ? 'border-purple-600 shadow-md'
                          : 'border-purple-200 hover:border-blue-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('Live Transcripts & Alerts', 'tool') ? 'text-purple-900 font-bold' : 'text-gray-700'}`}
                      >
                        Live Transcripts & Alerts
                      </div>
                    </div>
                  </div>
                </div>

                {/* Layer 4: Data Sources */}
                <div
                  className={`px-6 py-5 bg-gray-150 transition-all duration-1000 ${
                    visibleLayers[4] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                >
                  <div className="text-xs font-bold text-gray-900 mb-4">Data Sources</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div
                      className={`border rounded bg-gradient-to-b from-green-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('Bills', 'data')
                          ? 'border-green-600 shadow-md'
                          : 'border-green-200 hover:border-green-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('Bills', 'data') ? 'text-green-900 font-bold' : 'text-gray-700'}`}
                      >
                        Bills
                      </div>
                    </div>
                    <div
                      className={`border rounded bg-gradient-to-b from-green-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('Campaign Finance Contributions', 'data')
                          ? 'border-green-600 shadow-md'
                          : 'border-green-200 hover:border-green-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('Campaign Finance Contributions', 'data') ? 'text-green-900 font-bold' : 'text-gray-700'}`}
                      >
                        Campaign Finance Contributions
                      </div>
                    </div>
                    <div
                      className={`border rounded bg-gradient-to-b from-green-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('Statutes & Regulations', 'data')
                          ? 'border-green-600 shadow-md'
                          : 'border-green-200 hover:border-green-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('Statutes & Regulations', 'data') ? 'text-green-900 font-bold' : 'text-gray-700'}`}
                      >
                        Statutes & Regulations
                      </div>
                    </div>
                    <div
                      className={`border rounded bg-gradient-to-b from-green-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('Committee & Chamber Hearings', 'data')
                          ? 'border-green-600 shadow-md'
                          : 'border-green-200 hover:border-green-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('Committee & Chamber Hearings', 'data') ? 'text-green-900 font-bold' : 'text-gray-700'}`}
                      >
                        Committee & Chamber Hearings
                      </div>
                    </div>
                    <div
                      className={`border rounded bg-gradient-to-b from-green-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('Committees', 'data')
                          ? 'border-green-600 shadow-md'
                          : 'border-green-200 hover:border-green-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('Committees', 'data') ? 'text-green-900 font-bold' : 'text-gray-700'}`}
                      >
                        Committees
                      </div>
                    </div>
                    <div
                      className={`border rounded bg-gradient-to-b from-green-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('Members', 'data')
                          ? 'border-green-600 shadow-md'
                          : 'border-green-200 hover:border-green-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('Members', 'data') ? 'text-green-900 font-bold' : 'text-gray-700'}`}
                      >
                        Members
                      </div>
                    </div>
                    <div
                      className={`border rounded bg-gradient-to-b from-green-50/40 to-white px-2.5 py-2 text-center transition-all ${
                        isHighlighted('Lobbyists', 'data')
                          ? 'border-green-600 shadow-md'
                          : 'border-green-200 hover:border-green-400'
                      }`}
                    >
                      <div
                        className={`text-[11px] font-medium ${isHighlighted('Lobbyists', 'data') ? 'text-green-900 font-bold' : 'text-gray-700'}`}
                      >
                        Lobbyists
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Tagline */}
              <div className="text-center">
                <p className="font-serif text-2xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
                  <span className="block">
                    LegisIQ delivers everything you need to influence policy.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ProblemSolution Section - Built for how you actually work */}
        <section className="py-32 bg-white">
          <div className="container mx-auto px-4">
            {/* Section Header */}
            <div
              ref={problemHeaderRef}
              className="text-center mb-24 max-w-4xl mx-auto transition-all duration-1000"
              style={{
                opacity: problemHeaderVisible ? 1 : 0,
                transform: problemHeaderVisible ? 'translateY(0)' : 'translateY(30px)',
              }}
            >
              <h2 className="text-5xl md:text-6xl font-bold text-[#102A43] mb-6">
                Built for how you actually work
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Policy professionals don't have time for fragmented tools. LegisIQ delivers only
                what you need, when you need it.
              </p>
            </div>

            {/* Problem 1: Bill Tracking */}
            <div
              ref={(el) => (problemRefs.current[0] = el)}
              className="mb-32 transition-all duration-1000"
              style={{
                opacity: visibleProblems.includes(0) ? 1 : 0,
                transform: visibleProblems.includes(0) ? 'translateY(0)' : 'translateY(50px)',
              }}
            >
              <div className="flex flex-col lg:grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
                {/* Content */}
                <div className="order-1">
                  <div className="mb-8">
                    <div className="inline-block px-4 py-2 bg-red-50 border border-red-200 rounded-full mb-4">
                      <span className="text-sm font-semibold text-red-700">The Problem</span>
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold text-[#102A43] mb-6">
                      Reading every bill to identify pertinent legislation
                    </h3>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      Government affairs teams waste hundreds of hours manually reviewing thousands
                      of bills each session, struggling to identify which legislation actually
                      impacts their organization before it's too late to act.
                    </p>
                  </div>
                  <div className="pt-8 border-t border-gray-200">
                    <div className="inline-block px-4 py-2 bg-green-50 border border-green-200 rounded-full mb-4">
                      <span className="text-sm font-semibold text-green-700">The Solution</span>
                    </div>
                    <h4 className="text-2xl md:text-3xl font-bold text-[#102A43] mb-4">
                      Bill Agent
                    </h4>
                    <p className="text-lg text-gray-700 leading-relaxed mb-6">
                      Automatically tracking. If we identify it as needing to be tracked then we
                      pass it down to the lobbyist in the state to make sure they are tracking.
                    </p>
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">
                        Key Features
                      </p>
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-green-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">Automated Bill Tracking</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-green-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">Bill Version Comparison</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-green-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">Real-time Text Alerts</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-green-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">Historical Bill Analysis</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Visual */}
                <div className="relative flex items-center justify-center order-2">
                  <div className="w-80 h-80 rounded-full flex items-center justify-center shadow-2xl border-4 border-white overflow-hidden bg-white">
                    <img
                      src="/bill_agent.png"
                      alt="Bill Tracking"
                      className="w-full h-full object-contain scale-110"
                    />
                  </div>
                  <div className="absolute -bottom-6 -right-6 bg-white border-4 border-white rounded-2xl shadow-2xl px-8 py-4">
                    <div className="text-3xl font-bold text-[#102A43]">Real-time</div>
                    <div className="text-xs text-gray-600 uppercase tracking-wide">
                      alerts & tracking
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Problem 2: Transcript Analysis */}
            <div
              ref={(el) => (problemRefs.current[1] = el)}
              className="mb-32 transition-all duration-1000"
              style={{
                opacity: visibleProblems.includes(1) ? 1 : 0,
                transform: visibleProblems.includes(1) ? 'translateY(0)' : 'translateY(50px)',
              }}
            >
              <div className="flex flex-col lg:grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
                {/* Content */}
                <div className="order-1 lg:order-2">
                  <div className="mb-8">
                    <div className="inline-block px-4 py-2 bg-red-50 border border-red-200 rounded-full mb-4">
                      <span className="text-sm font-semibold text-red-700">The Problem</span>
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold text-[#102A43] mb-6">
                      Spending countless hours watching committee and floor meetings
                    </h3>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      Policy teams spend entire days monitoring live committee hearings and floor
                      sessions, trying to catch every mention of their bills or issues—time that
                      could be spent on strategic advocacy and relationship building.
                    </p>
                  </div>
                  <div className="pt-8 border-t border-gray-200">
                    <div className="inline-block px-4 py-2 bg-green-50 border border-green-200 rounded-full mb-4">
                      <span className="text-sm font-semibold text-green-700">The Solution</span>
                    </div>
                    <h4 className="text-2xl md:text-3xl font-bold text-[#102A43] mb-4">
                      Committee Agent
                    </h4>
                    <p className="text-lg text-gray-700 leading-relaxed mb-6">
                      <em>"You may want to hop in on this committee this week."</em> Alerts you to
                      relevant committee hearings, events, etc.
                    </p>
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">
                        Key Features
                      </p>
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-green-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">Live Transcripts (50 states)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-green-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">AI Driven Impact Reports</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-green-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">Committee Hearing Coverage</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-green-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">Instant Video Clips</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Visual */}
                <div className="relative flex items-center justify-center order-2 lg:order-1">
                  <div className="w-80 h-80 rounded-full flex items-center justify-center shadow-2xl border-4 border-white overflow-hidden bg-white">
                    <img
                      src="/transcript_agent.png"
                      alt="Transcript Analysis"
                      className="w-full h-full object-contain scale-110"
                    />
                  </div>
                  <div className="absolute -bottom-6 -right-6 bg-white border-4 border-white rounded-2xl shadow-2xl px-8 py-4">
                    <div className="text-3xl font-bold text-[#102A43]">10 hrs</div>
                    <div className="text-xs text-gray-600 uppercase tracking-wide">
                      saved weekly per client
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Problem 3: Advocacy Management */}
            <div
              ref={(el) => (problemRefs.current[2] = el)}
              className="transition-all duration-1000"
              style={{
                opacity: visibleProblems.includes(2) ? 1 : 0,
                transform: visibleProblems.includes(2) ? 'translateY(0)' : 'translateY(50px)',
              }}
            >
              <div className="flex flex-col lg:grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
                {/* Content */}
                <div className="order-1">
                  <div className="mb-8">
                    <div className="inline-block px-4 py-2 bg-red-50 border border-red-200 rounded-full mb-4">
                      <span className="text-sm font-semibold text-red-700">The Problem</span>
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold text-[#102A43] mb-6">
                      Managing a sprawled advocate stable across the country
                    </h3>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      Coordinating dozens of lobbyists across multiple states becomes a logistical
                      nightmare—tracking who's covering which committees, monitoring testimony, and
                      ensuring strategic alignment while maintaining visibility into relationships
                      and contributions.
                    </p>
                  </div>
                  <div className="pt-8 border-t border-gray-200">
                    <div className="inline-block px-4 py-2 bg-green-50 border border-green-200 rounded-full mb-4">
                      <span className="text-sm font-semibold text-green-700">The Solution</span>
                    </div>
                    <h4 className="text-2xl md:text-3xl font-bold text-[#102A43] mb-4">
                      Advocacy Management Portal
                    </h4>
                    <p className="text-lg text-gray-700 leading-relaxed mb-6">
                      Leverage your lobbyist stable to its fullest extent with our comprehensive
                      management portal that tracks relationships, testimony, and strategic coverage
                      across all chambers and committees.
                    </p>
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">
                        Key Features
                      </p>
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-green-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">
                          Complete lobbyist relationship mapping
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-green-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">
                          Testimony monitoring and analysis
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-green-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">
                          Strategic chamber coverage optimization
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-green-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">
                          Contribution tracking and analysis
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Visual */}
                <div className="relative flex items-center justify-center order-2">
                  <div className="w-80 h-80 rounded-full flex items-center justify-center shadow-2xl border-4 border-white overflow-hidden bg-white">
                    <img
                      src="/lobby_agent.png"
                      alt="Advocacy Management Portal"
                      className="w-full h-full object-contain scale-110"
                    />
                  </div>
                  <div className="absolute -bottom-6 -right-6 bg-white border-4 border-white rounded-2xl shadow-2xl px-8 py-4">
                    <div className="text-3xl font-bold text-[#102A43]">50-state</div>
                    <div className="text-xs text-gray-600 uppercase tracking-wide">
                      advocate tracking
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Box - Explore Complete Product Suite */}
            <div
              ref={(el) => (problemRefs.current[3] = el)}
              className="mt-32 transition-all duration-1000"
              style={{
                opacity: visibleProblems.includes(3) ? 1 : 0,
                transform: visibleProblems.includes(3) ? 'translateY(0)' : 'translateY(50px)',
              }}
            >
              <div className="max-w-5xl mx-auto">
                <div className="bg-gradient-to-b from-white to-slate-50/50 border border-gray-100 rounded-2xl p-12">
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto text-center">
                    Explore our complete product suite with 15+ tools for legislative intelligence,
                    advocacy management, and AI-powered research.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <button
                      onClick={() => {
                        window.open('https://calendly.com/william-legisiq/30min', '_blank');
                      }}
                      className="w-full sm:w-auto px-8 py-4 text-base font-semibold text-[#102A43] border-2 border-[#102A43] hover:bg-[#102A43] hover:text-white rounded-xl transition-all duration-300">
                      Tell Us Your Challenge
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Client Testimonials Section */}
        <section className="py-32 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold text-[#102A43] mb-4">
                  What Our Clients Say
                </h2>
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                {/* John Williams Testimonial */}
                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                  <blockquote className="text-gray-600 italic mb-6">
                    "The tools that I use on LegisIQ are the BEST! I have used other services and
                    they don't compare. I have had such significant positive feedback from clients
                    about the bill comparison tool and the bill brief is pretty incredible too."
                  </blockquote>
                  <div className="flex items-center gap-3 pt-6 border-t border-gray-200">
                    <img
                      src="/jw_avatar.jpeg"
                      alt="John Williams"
                      className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                    />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">John Williams</p>
                      <p className="text-xs text-gray-500">Principal, Capital Consulting Group</p>
                    </div>
                  </div>
                </div>

                {/* Matt B. Testimonial */}
                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                  <blockquote className="text-gray-600 italic mb-6">
                    "I've been using the AI tools for the past year and the entire suite has
                    transformed my workflow. From quickly sifting through committee testimony to
                    find relevant bills, to using their chat feature for instant bill research, to
                    leveraging the ai comparison tool to break down legislative changes—every new
                    feature has made a meaningful impact on how I work."
                  </blockquote>
                  <div className="flex items-center gap-3 pt-6 border-t border-gray-200">
                    <img
                      src="/matt_avatar.jpeg"
                      alt="Matt B."
                      className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                    />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Matt B.</p>
                      <p className="text-xs text-gray-500">
                        Owner/Lobbyist, Bentley Public Affairs
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pete Martinez Testimonial */}
                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                  <blockquote className="text-gray-600 italic mb-6">
                    "These tools give us enhanced capabilities to keep our clients up-to-speed--in
                    real time--with what's happening at the Capitol. From text alerts to the new AI
                    tools that allow us to more efficiently analyze pending legislation and proposed
                    revisions, the platform is critical to our operations and providing better
                    service to our clients."
                  </blockquote>
                  <div className="flex items-center gap-3 pt-6 border-t border-gray-200">
                    <img
                      src="/pete_avatar.jpeg"
                      alt="Pete Martinez"
                      className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                    />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Pete Martinez</p>
                      <p className="text-xs text-gray-500">
                        Managing Partner, Martinez Strategic Alliances
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CaseStudy Section */}
        <section className="py-32 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              {/* Header */}
              <div className="text-center mb-20">
                <div className="inline-block px-4 py-2 bg-blue-50 border border-blue-200 rounded-full mb-6">
                  <span className="text-sm font-semibold text-[#102A43]">CLIENT SUCCESS STORY</span>
                </div>
                <h2 className="text-5xl md:text-6xl font-bold text-[#102A43] mb-6">
                  Real Results from Day One
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  See how LegisCon Louisiana transformed their 50-year-old business with LegisIQ's
                  AI-powered platform
                </p>
              </div>

              {/* Content Grid */}
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                {/* Left: Logo & Metrics */}
                <div>
                  <div className="bg-white rounded-3xl p-12 shadow-2xl border border-gray-200">
                    <div className="flex items-center justify-center mb-12 pb-8 border-b border-gray-200">
                      <img
                        src="/legiscon-logo-from-sales.png"
                        alt="LegisCon Louisiana"
                        className="w-full max-w-sm"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4 md:gap-6">
                      <div className="text-center">
                        <div className="text-3xl md:text-5xl font-bold text-[#102A43] mb-2">5x</div>
                        <div className="text-xs md:text-sm text-gray-600 leading-tight">
                          Quarterly
                          <br />
                          Growth
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl md:text-5xl font-bold text-[#102A43] mb-2">
                          20%
                        </div>
                        <div className="text-xs md:text-sm text-gray-600 leading-tight">
                          Annual
                          <br />
                          Growth
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl md:text-5xl font-bold text-[#102A43] mb-2">
                          10hrs
                        </div>
                        <div className="text-xs md:text-sm text-gray-600 leading-tight">
                          Saved
                          <br />
                          Weekly
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: Quote */}
                <div>
                  <blockquote className="mb-10">
                    <p className="text-2xl md:text-3xl font-medium text-gray-800 leading-relaxed mb-6">
                      "We launched these tools in Louisiana and quintupled our quarterly growth rate
                      in the first three months of 2025."
                    </p>
                    <p className="text-xl text-gray-600 leading-relaxed mb-8">
                      "Our 50-year-old business grew by over 20% in a single year. Clients report
                      saving up to 10 hours per week thanks to our transcript services."
                    </p>
                  </blockquote>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BottomCTA Section */}
        <section className="py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
          <div className="relative container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight text-white">
                Ready to transform your workflow?
              </h2>
              <p className="text-2xl text-gray-300 mb-12 leading-relaxed">
                See how our agents and tools work for you—24/7.
              </p>
              <button
                onClick={() => {
                  window.open('https://calendly.com/william-legisiq/30min', '_blank');
                }}
                className="group bg-white text-gray-900 hover:bg-gray-100 px-12 py-6 text-xl font-bold rounded-xl shadow-2xl transition-all duration-300 hover:scale-105">
                Get Started Today
                <span className="inline-block ml-2 transition-transform group-hover:translate-x-1">
                  →
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* Blog Section */}
        {/* <BlogSection />

        <JoinInfoSection /> */}
      </div>
    </SplashLayout>
  );
}
